"use client";

import Image from "next/image";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons, Images } from "@assets/assets";
import { useTheme } from "@/HomeFeatures/Context/ThemeContext";

interface PricingRow {
  solutionType: string;
  timeline: string;
  cost: string;
}

interface PricingTableData {
  title: string;
  description: string;
  tableHeaders: {
    solutionType: string;
    timeline: string;
    cost: string;
  };
  rows: PricingRow[];
  note: React.ReactNode;
  ctaHeading: string;
  ctaSubheading: string;
}

interface Props {
  data: PricingTableData;
}

export default function PricingGuide({ data }: Props) {
  const { handleOpenQuote } = useTheme();

  const {
    title,
    description,
    tableHeaders,
    rows,
    note,
    ctaHeading,
    ctaSubheading,
  } = data;

  return (
    <section className="py-[60px] sm:py-[75px] lg:py-[100px] bg-[#D9E2ED]">
      <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)]">
        {/* Heading */}
        <h2 className="clash-font text-[clamp(30px,4vw,64px)] font-medium leading-[110%] text-[#00040B]">
          {title}
        </h2>

        <p className="clash-font mt-4 sm:mt-5 lg:mt-6 text-[15px] sm:text-[18px] lg:text-[28px] font-regular leading-[150%] lg:leading-[160%] text-[#00040B]/70">
          {description}
        </p>

        {/* Table + CTA */}
        <div className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-1 lg:grid-cols-[3fr_1fr] gap-5 lg:gap-6 items-stretch">
          {/* Table */}
          <div className="flex flex-col h-full min-w-0">
            <div className="overflow-x-auto overflow-y-hidden border border-[#E8EDF4] rounded-[12px] flex-1 [-webkit-overflow-scrolling:touch]">
              <table className="w-full min-w-[680px] lg:min-w-0 h-full border-collapse">
                <thead>
                  <tr className="bg-[#225BD4] text-white">
                    <th className="w-1/3 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 text-[14px] sm:text-[17px] lg:text-[20px] font-medium text-center border-r border-white/20">
                      {tableHeaders.solutionType}
                    </th>

                    <th className="w-1/3 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 text-[14px] sm:text-[17px] lg:text-[20px] font-medium text-center border-r border-white/20">
                      {tableHeaders.timeline}
                    </th>

                    <th className="w-1/3 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 text-[14px] sm:text-[17px] lg:text-[20px] font-medium text-center">
                      {tableHeaders.cost}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "bg-[#ECF0F6]" : "bg-white"}
                    >
                      <td className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 text-[14px] sm:text-[16px] lg:text-[18px] text-center border font-medium border-[#EEF2F7] text-[#00040B]">
                        {row.solutionType}
                      </td>

                      <td className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 text-[14px] sm:text-[16px] lg:text-[18px] text-center border font-medium border-[#EEF2F7] text-[#00040B]">
                        {row.timeline}
                      </td>

                      <td className="px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 text-[14px] sm:text-[16px] lg:text-[18px] text-center border font-medium border-[#EEF2F7] text-[#00040B]">
                        {row.cost}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile scroll hint */}
            <p className="mt-2 text-[12px] text-[#00040B]/40 sm:hidden">
              Swipe horizontally to view the full table
            </p>

            {/* Note */}
            <div className="mt-3 sm:mt-4 bg-white border border-[#E8EDF4] rounded-[12px] px-4 sm:px-6 lg:px-8 py-3 sm:py-4 text-center text-[12px] sm:text-[14px] lg:text-[16px] leading-[150%] text-[#00040B]/80">
              {note}
            </div>
          </div>

          {/* CTA box */}
          <div className="bg-[#225BD4] rounded-[12px] px-6 sm:px-8 lg:p-12 py-8 sm:py-10 lg:py-12 flex flex-col items-center justify-start md:justify-between text-center relative md:min-h-[420px] lg:h-full">
            <div className="z-10 flex flex-col items-center space-y-4 sm:space-y-5 lg:space-y-6">
              <h3 className="text-[26px] sm:text-[30px] lg:text-[35px] font-semibold clash-font text-white leading-[115%] lg:leading-tight max-w-[14ch] lg:max-w-[12ch]">
                {ctaHeading}
              </h3>

              <p className="text-white text-[15px] sm:text-[17px] lg:text-[18px] font-medium max-w-[28ch] sm:max-w-[26ch] lg:max-w-[24ch] clash-font leading-[150%]">
                {ctaSubheading}
              </p>

              <PrimaryBtn
                text="Let's Connect"
                dark={true}
                iconSrc={Icons?.chevronBlue}
                onClick={() => {
                  handleOpenQuote();
                }}
              />
            </div>

            <div className="mt-2 lg:mt-6 z-10">
              <Image
                src={Images?.laravel_need_a_price}
                alt="Get quote"
                width={180}
                height={70}
                className="w-[140px] sm:w-[160px] lg:w-[180px] h-auto"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
